import { useEffect, useState } from 'react';
import {
  CONTRACT_TYPES,
  type ContractType,
  type Metafield,
  type Project,
} from '@forecastify/shared';
import { Button, Dialog, Select, Stepper, TextInput } from '@forecastify/ui';
import { useT, type MessageKey } from '../../i18n';
import { Field } from '../card';
import { MetafieldSection } from '../metafields/metafield-section';
import { DateField } from '../date-field';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Új szerződés — `2fazis.markup.html` 4300–4341.                            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **C24 SHIPPED THE LIST AND THE DETAIL AND NO WAY TO CREATE ONE.** The entity was reachable
 * only over HTTP: every screen that reads a contract worked, and a workspace nobody had seeded by
 * hand saw an empty state with nothing to click. The mockup has drawn this button in two places
 * since the beginning — the toolbar at 814 and the empty state at 875 — and both were transcribed
 * as decoration. That is what this file corrects.
 *
 * ⚠️ **No status field.** A contract is born `draft`; the mockup's own description says so
 * (*"Tervezet státuszban jön létre"*), and the status card on the detail is where it moves on.
 *
 * ⚠️ **No owner field either, and the mockup has none.** `newContract` opens with `owner: ME` and
 * the dialog never asks — so the caller supplies the current member and the detail reassigns.
 * `POST /contracts` requires `ownerMemberId`, so somebody has to answer; defaulting to the person
 * filling in the form is the answer that needs no control.
 *
 * ⚠️ **Metafields are C30's**, and their absence here is deliberate rather than forgotten —
 * `PHASE-2.md` lists `cfdMeta` among the keys the mockup draws that nothing is wired to yet.
 */
export interface ContractDraft {
  /**
   * C30 · the workspace's `contract` metafields, keyed by the field's own key.
   *
   * ⚠️ Sent only when the workspace has defined fields — an empty object CLEARS every value, so an
   * absent one is what "leave them alone" looks like on the wire.
   */
  metafields: Record<string, unknown>;
  name: string;
  /**
   * ┌──────────────────────────────────────────────────────────────────────┐
   * │ C56 · ⚠️⚠️ `scope` LIVES HERE AND NOWHERE ELSE. It is a control, not a column. │
   * └──────────────────────────────────────────────────────────────────────┘
   *
   * `PHASE-5.md` §2.5 reads it as a field on the contract; the mockup's own `cf.scope` is a radio
   * in this dialog and its stored row has no such thing — `0153` says why that is the better
   * shape. So the draft carries it, the body does not, and what reaches the server is a
   * `projectId` or a `clientId`.
   */
  scope: 'project' | 'client';
  projectId: string;
  clientId: string;
  type: ContractType;
  startsOn: string;
  endsOn: string;
  /**
   * ⚠️ How often a recurring contract recurs — asked HERE since 14 Oct 2026 (ux-pass-2). It was
   * created monthly and left for the detail's card to refine, so a weekly retainer started life
   * with a year of monthly periods. Ignored for any other type.
   */
  recurrenceUnit: 'month' | 'week';
  /** Every *n* units, 1–12 — the card's own bounds. */
  recurrenceStep: string;
}

const TYPE_KEY: Readonly<Record<ContractType, MessageKey>> = {
  recurring: 'contract.type.recurring',
  milestone: 'contract.type.milestone',
  service: 'contract.type.service',
};

export function ContractDialog({
  open,
  projects,
  clients,
  workFreeRevenue,
  defaultProjectId,
  onClose,
  onSubmit,
  serverError,
  metafields,
  members,
}: {
  readonly open: boolean;
  readonly projects: readonly Project[];
  /** C56 · the clients a contract may hang off, for the `client` scope. */
  readonly clients: readonly { readonly id: string; readonly name: string }[];
  /**
   * C56 · ⚠️ **`contracts.work_free_revenue`, the TIER.** C55's settings row gates both halves
   * of one capability; without it the scope switch is not drawn at all, because the server would
   * refuse the only thing it lets somebody choose.
   */
  readonly workFreeRevenue: boolean;
  /** Every contract already in the workspace — the code preview counts them. */
  /* ⚠️⚠️ **NO `contracts` ANY MORE — round 5 item 18.** The whole contract list was passed in
     so the dialog could guess the next code and print it; with that gone, the only thing it did
     was make two screens fetch a list to hand to a form that ignored it. */
  /** Opened from a project's own tab, the project is decided and the field is locked. */
  readonly defaultProjectId?: string;
  readonly onClose: () => void;
  readonly onSubmit: (draft: ContractDraft) => void;
  readonly serverError: string | null;
  /** The workspace's fields for this entity. Empty when none are defined. */
  readonly metafields: readonly Metafield[];
  /** For the `user` metafield type. */
  readonly members: readonly { readonly id: string; readonly name: string }[];
}) {
  const t = useT();
  const [draft, setDraft] = useState<ContractDraft>(() => emptyDraft(projects, defaultProjectId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setDraft(emptyDraft(projects, defaultProjectId));
  }, [open, projects, defaultProjectId]);

  /*
   * ⚠️⚠️ **THE CODE PREVIEW WENT WITH THE SENTENCE ABOVE IT — round 5 item 18.** It computed
   * the next `HSO-C0n` from the contracts in hand and printed *"A szerződés kódja: HSO-C02"* under
   * the form. The argument for it was that a form showing the consequence of a choice is easier to
   * fill in, which was true and is outweighed by two things: it narrated a field nobody had filled
   * in, and the number was a GUESS — `nextCode` runs inside the create transaction against
   * `max(...) + 1`, so two people filling this in at once both saw `HSO-C03` and one got `HSO-C04`.
   */

  const patch = (part: Partial<ContractDraft>): void => {
    setDraft((current) => ({ ...current, ...part }));
    setError(null);
  };

  const submit = (): void => {
    if (draft.name.trim().length === 0) {
      setError(t('contract.error.name'));
      return;
    }
    if (draft.scope === 'project' && draft.projectId === '') {
      setError(t('contract.error.project'));
      return;
    }
    /* ⚠️ A client-scoped contract needs a client for the same reason — `0153`'s subject
       constraint refuses a contract with neither, and the dialog should say so first. */
    if (draft.scope === 'client' && draft.clientId === '') {
      setError(t('contract.error.client'));
      return;
    }
    if (draft.startsOn === '') {
      setError(t('contract.error.start'));
      return;
    }
    /* String comparison, and a real date comparison because these are wire dates. */
    if (draft.endsOn !== '' && draft.endsOn < draft.startsOn) {
      setError(t('contract.error.dates'));
      return;
    }
    onSubmit(draft);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={t('contract.dialog.new')}
      closeLabel={t('common.close')}
    >
      <div className="flex flex-col gap-3">
        <Field required label={t('contract.field.name')}>
          <TextInput
            value={draft.name}
            onChange={(event) => {
              patch({ name: event.target.value });
            }}
            placeholder={t('contract.field.namePlaceholder')}
          />
        </Field>

        {/**
         * ⚠️⚠️ **AHEAD OF THE PICKER, WHICH IS THE MOCKUP'S ORDER AND THE RIGHT ONE.** The
         * switch decides WHICH picker is drawn, so putting it after would mean changing an answer
         * somebody had already given. Hidden entirely when the dialog opens inside a project —
         * there is no choice to make there.
         *
         * ⚠️ **And hidden without the flag.** `contracts.work_free_revenue` gates creating a
         * client-scoped contract (C55's row covers both halves), so offering the option to a
         * workspace that cannot use it would be offering a refusal.
         */}
        {defaultProjectId === undefined && workFreeRevenue ? (
          <Field label={t('contract.field.scope')}>
            <Select
              value={draft.scope}
              onChange={(event) => {
                patch({ scope: event.target.value === 'client' ? 'client' : 'project' });
              }}
              options={[
                { value: 'project', label: t('contract.scope.onProject') },
                { value: 'client', label: t('contract.scope.onClient') },
              ]}
            />
          </Field>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          {draft.scope === 'client' ? (
            <Field required label={t('contract.column.client')}>
              <Select
                /* U-14 · a list of records, so it searches. */
                searchable
                searchPlaceholder={t('list.searchOptions')}
                value={draft.clientId}
                onChange={(event) => {
                  patch({ clientId: event.target.value });
                }}
                options={[
                  { value: '', label: t('common.choose') },
                  ...clients.map((client) => ({ value: client.id, label: client.name })),
                ]}
              />
            </Field>
          ) : (
            <Field required label={t('contract.column.project')}>
              <Select
                /* U-14 · a list of records, so it searches. */
                searchable
                searchPlaceholder={t('list.searchOptions')}
                value={draft.projectId}
                disabled={defaultProjectId !== undefined}
                onChange={(event) => {
                  patch({ projectId: event.target.value });
                }}
                options={projects.map((project) => ({ value: project.id, label: project.name }))}
              />
            </Field>
          )}

          <Field label={t('contract.column.type')}>
            <Select
              value={draft.type}
              onChange={(event) => {
                patch({ type: event.target.value as ContractType });
              }}
              options={CONTRACT_TYPES.map((type) => ({ value: type, label: t(TYPE_KEY[type]) }))}
            />
          </Field>

          <Field required label={t('contract.column.startsOn')}>
            <DateField
              label={t('contract.column.startsOn')}
              value={draft.startsOn}
              clearable={false}
              onChange={(startsOn) => {
                patch({ startsOn });
              }}
            />
          </Field>

          {/* ⚠️ The optional note reads *nyitott* rather than *opcionális*: a framework contract
              with no end has not got a missing date, it has deliberately no end — the same
              distinction the list column makes. */}
          <Field label={t('contract.column.endsOn')} hint={t('contract.openEnded')}>
            <DateField
              label={t('contract.column.endsOn')}
              value={draft.endsOn}
              onChange={(endsOn) => {
                patch({ endsOn });
              }}
            />
          </Field>
        </div>

        {/* ⚠️ The unit and the step; the run starts with the contract and ends with it, which the
            detail's card can still change. `contracts_recurrence_shape` refuses a recurring
            contract without a recurrence, so these always travel. */}
        {draft.type === 'recurring' ? (
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('contract.recurrence.unit')}>
              <Select
                value={draft.recurrenceUnit}
                onChange={(event) => {
                  patch({ recurrenceUnit: event.target.value as 'month' | 'week' });
                }}
                options={[
                  { value: 'month', label: t('contract.recurrence.month') },
                  { value: 'week', label: t('contract.recurrence.week') },
                ]}
              />
            </Field>
            <Field label={t('contract.recurrence.step')} hint={t('contract.dialog.stepHint')}>
              <Stepper
                min={1}
                max={12}
                aria-label={t('contract.recurrence.step')}
                decrementLabel={t('common.decrease')}
                incrementLabel={t('common.increase')}
                value={draft.recurrenceStep}
                onChange={(next) => {
                  patch({ recurrenceStep: next });
                }}
              />
            </Field>
          </div>
        ) : null}

        {/* ⚠️ Its own box, below the product's own fields — a workspace's additions come after
            what the product itself defines. Renders nothing when none are defined. */}
        <MetafieldSection
          fields={metafields}
          values={draft.metafields}
          members={members}
          onChange={(values) => {
            patch({ metafields: values });
          }}
        />

        {error === null && serverError === null ? null : (
          <div className="text-destructive text-[12px]" role="alert">
            {error ?? serverError}
          </div>
        )}

        <div className="mt-1 flex items-center gap-2">
          <div className="flex-1" />
          <Button variant="outline" size="lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="lg" onClick={submit}>
            {t('contract.action.create')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

function emptyDraft(projects: readonly Project[], defaultProjectId?: string): ContractDraft {
  return {
    name: '',
    scope: 'project',
    projectId: defaultProjectId ?? projects[0]?.id ?? '',
    /* ⚠️ Empty rather than the first client: a contract's client is a decision, and the
       project picker defaults because opening inside a project has already made that one. */
    clientId: '',
    /* The mockup's `newContract` opens on `service`, starting today. */
    type: 'service',
    startsOn: new Date().toISOString().slice(0, 10),
    endsOn: '',
    metafields: {},
    recurrenceUnit: 'month',
    recurrenceStep: '1',
  };
}

/** The recurrence a new recurring contract is created with — the dialog's unit and step. */
export function draftRecurrence(draft: ContractDraft) {
  return {
    unit: draft.recurrenceUnit,
    step: Math.max(1, Math.min(12, Math.round(Number(draft.recurrenceStep)) || 1)),
    startsOn: draft.startsOn,
    endMode: 'date' as const,
    endsOn: draft.endsOn === '' ? null : draft.endsOn,
    count: null,
  };
}
